import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

interface SEOBlock {
    key?: string;
    title: string;
    content: string;
}

interface SEOSection {
    title: string;
    blocks: SEOBlock[];
}

interface SEOAccordionProps {
    blocks?: SEOBlock[];
    sections?: SEOSection[];
}

// Teilt jedem Item ein eigenes Icon zu
function getSEOToolIcon(key?: string) {
    switch (key) {
        case 'pagespeed':
            return (
                <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            );
        case 'sitemap':
            return (
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
            );
        case 'robots':
            return (
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
            );
        case 'metatitle':
            return (
                <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a.997.997 0 01-1.414 0l-7-7A1.997 1.997 0 013 12V7a4 4 0 014-4z" />
                </svg>
            );
        case 'metadescription':
            return (
                <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
            );
        case 'linkstatus_api':
            return (
                <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
            );
        case 'technical':
            return (
                <svg className="h-5 w-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            );
        case 'content':
            return (
                <svg className="h-5 w-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            );
        default:
            // Standard SEO Icon
            return (
                <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            );
    }
}

export function SEOAccordion({ blocks, sections }: SEOAccordionProps) {
    // Wenn sections verwendet werden, verwende die strukturierte Darstellung
    if (sections && sections.length > 0) {
        return (
            <div className="space-y-6">
                {sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="space-y-4">
                        {/* Zeige Sektion-Titel nur an, wenn es nicht "SEO Analyse Ergebnisse" ist */}
                        {section.title !== "SEO Analyse Ergebnisse" && (
                            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                                {section.title}
                            </h3>
                        )}
                        <div className="space-y-4">
                            {section.blocks.map((block, blockIndex) => {
                                // Spezielle Überschrift für technical Block
                                const showTechnicalHeader = block.key === 'technical';
                                const isFixedCard = block.key === 'technical' || block.key === 'content';

                                return (
                                    <div key={block.key || `${sectionIndex}-${blockIndex}`}>
                                        {showTechnicalHeader && (
                                            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">
                                                Technische und inhaltliche SEO Auswertung
                                            </h3>
                                        )}

                                        {/* Feste Kachel für technical und content */}
                                        {isFixedCard ? (
                                            <div className="overflow-hidden rounded-xl border-2 border-slate-300 bg-white shadow-md">
                                                <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b-2 border-slate-200">
                                                    <div className="flex-shrink-0">
                                                        {getSEOToolIcon(block.key)}
                                                    </div>
                                                    <span className="text-base font-semibold text-gray-900">
                                                        {block.title !== "SEO Analyse Ergebnisse" && block.title !== "SEO Analyse"
                                                            ? block.title
                                                            : "SEO Ergebnisse"
                                                        }
                                                    </span>
                                                </div>
                                                <div className="px-4 py-4">
                                                    <div
                                                        className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-a:text-blue-600 prose-strong:text-gray-900"
                                                        style={{
                                                            fontFamily: 'inherit',
                                                            color: 'inherit'
                                                        }}
                                                    >
                                                        <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                                                            {formatContent(block.content) || 'Keine Daten verfügbar'}
                                                        </ReactMarkdown>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Klappbares Accordion für alle anderen */
                                            <details
                                                className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                                                open={block.key === 'pagespeed' || (blockIndex === 0 && sectionIndex === 0)}
                                            >
                                                <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-shrink-0">
                                                            {getSEOToolIcon(block.key)}
                                                        </div>
                                                        <span className="text-base font-semibold text-gray-900">
                                                            {block.title !== "SEO Analyse Ergebnisse" && block.title !== "SEO Analyse"
                                                                ? block.title
                                                                : "SEO Ergebnisse"
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="ml-auto">
                                                        <svg className="h-5 w-5 text-gray-400 transform transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </div>
                                                </summary>
                                                <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                                                    <div
                                                        className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-a:text-blue-600 prose-strong:text-gray-900"
                                                        style={{
                                                            fontFamily: 'inherit',
                                                            color: 'inherit'
                                                        }}
                                                    >
                                                        <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                                                            {formatContent(block.content) || 'Keine Daten verfügbar'}
                                                        </ReactMarkdown>
                                                    </div>
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Fallback auf alte blocks-Darstellung
    if (!blocks || blocks.length === 0) {
        return <div className="text-center py-8 text-gray-500">Keine SEO-Daten verfügbar</div>;
    }

    return (
        <div className="space-y-4">
            {blocks.map((block, index) => (
                <details
                    key={block.key || index}
                    className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                    open={block.key === 'pagespeed' || index === 0}
                >
                    <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                                {getSEOToolIcon(block.key)}
                            </div>
                            <span className="text-base font-semibold text-gray-900">
                                {block.title !== "SEO Analyse Ergebnisse" && block.title !== "SEO Analyse"
                                    ? block.title
                                    : "SEO Ergebnisse"
                                }
                            </span>
                        </div>
                        <div className="ml-auto">
                            <svg className="h-5 w-5 text-gray-400 transform transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </summary>
                    <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                        <div
                            className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-a:text-blue-600 prose-strong:text-gray-900"
                            style={{
                                fontFamily: 'inherit',
                                color: 'inherit'
                            }}
                        >
                            <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                                {formatContent(block.content) || 'Keine Daten verfügbar'}
                            </ReactMarkdown>
                        </div>
                    </div>
                </details>
            ))}
        </div>
    );
}

// Fallback für HTML-basierte SEO-Daten
export function SEOHtmlDisplay({ htmlContent }: { htmlContent: string }) {
    return (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm p-4">
            <div
                className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-a:text-blue-600 prose-strong:text-gray-900"
                style={{
                    fontFamily: 'inherit',
                    color: 'inherit',
                    backgroundColor: 'transparent'
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
        </div>
    );
}

// Hilfsfunktion um SEO-Daten in die gewünschte Struktur zu transformieren
export function transformSEODataToSections(seoData: any): SEOSection[] {
    console.log('Debug - transformSEODataToSections called with:', seoData);
    console.log('Debug - seoData type:', typeof seoData);
    console.log('Debug - seoData keys:', seoData ? Object.keys(seoData) : 'no data');

    // Wenn bereits strukturierte Daten vorliegen
    if (seoData?.sections && Array.isArray(seoData.sections)) {
        return seoData.sections;
    }

    const sections: SEOSection[] = [];

    // Priorität 1: Wenn seoData direkt blocks enthält
    if (seoData?.blocks && Array.isArray(seoData.blocks)) {
        // Verwende die vorhandenen Blöcke ohne weitere Strukturierung
        sections.push({
            title: 'SEO Analyse Ergebnisse',
            blocks: seoData.blocks
        });
        console.log('Debug - Using blocks array:', seoData.blocks);
        return sections;
    }

    // Priorität 2: Strukturierte SEO-Daten mit bekannten Keys
    if (seoData && typeof seoData === 'object') {
        // Mapping für SEO-Tools mit ihren deutschen Titeln
        const toolMapping: { [key: string]: string } = {
            'pagespeed': 'Google Page Speed Insights',
            'sitemap': 'Sitemap',
            'robots': 'robots.txt',
            'metatitle': 'Meta Title',
            'metadescription': 'Meta Description',
            'linkstatus_api': 'Broken Link Check (API)',
            'technical': 'On-Page Technical Audit',
            'content': 'SEO Content Audit'
        };

        // Sammle alle verfügbaren Blöcke
        const allBlocks: SEOBlock[] = [];

        Object.entries(seoData).forEach(([key, value]) => {
            // Überspringe Meta-Felder und leere Werte
            if (['meta', 'timestamp', 'version', 'id', '__typename'].includes(key.toLowerCase()) ||
                !value || (typeof value === 'string' && value.trim() === '')) {
                return;
            }

            let content: string;
            let title = toolMapping[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/[-_]/g, ' ');

            // Verarbeite verschiedene Datentypen
            if (typeof value === 'string') {
                content = value;
            } else if (typeof value === 'object') {
                // Type Guards für Object-Properties
                const valueObj = value as any;
                if (valueObj.content && typeof valueObj.content === 'string') {
                    content = valueObj.content;
                } else if (valueObj.html && typeof valueObj.html === 'string') {
                    content = valueObj.html;
                } else if (valueObj.text && typeof valueObj.text === 'string') {
                    content = valueObj.text;
                } else {
                    // Als JSON darstellen, wenn keine bekannte Struktur
                    content = JSON.stringify(value, null, 2);
                }
            } else {
                content = String(value);
            }

            // Füge den Block hinzu, wenn Inhalt vorhanden ist
            if (content && content.trim() !== '') {
                allBlocks.push({
                    key: key,
                    title: title,
                    content: content
                });
            }
        });

        // Erstelle eine einzige Sektion mit allen Blöcken
        if (allBlocks.length > 0) {
            sections.push({
                title: 'SEO Analyse Ergebnisse',
                blocks: allBlocks
            });
        }

        console.log('Debug - Created blocks from structured data:', allBlocks.length);
        return sections;
    }

    // Priorität 3: Wenn seoData ein HTML-String ist
    if (typeof seoData === 'string' && seoData.trim() !== '') {
        sections.push({
            title: 'SEO Analyse Ergebnisse',
            blocks: [{
                key: 'general',
                title: 'SEO Analyse Ergebnisse',
                content: seoData
            }]
        });
        console.log('Debug - Using string data');
        return sections;
    }

    // Priorität 4: Wenn seoData HTML-Content hat
    if (seoData?.html || seoData?.htmlContent) {
        const htmlContent = seoData.html || seoData.htmlContent;
        sections.push({
            title: 'SEO Analyse Ergebnisse',
            blocks: [{
                key: 'html-content',
                title: 'SEO Analyse Ergebnisse',
                content: htmlContent
            }]
        });
        console.log('Debug - Using HTML content');
        return sections;
    }

    console.log('Debug - No valid SEO data found, seoData:', seoData);
    return sections;
}

// Hilfsfunktion um Inhalte zu formatieren
function formatContent(content: string): string {
    if (!content) return content;

    return content;
}
